import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { ReactNode, useMemo } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  ALL_PAYERS_LABEL,
  conversionMessage,
} from "../helpers/ruleApplicability";

/**
 * CLE-2842 step 2 — the Payer-Provider / Typical / Fallback choice for the six additive rule types
 * (MPPR, Case Rate, Package, Exploding Fee Schedule, Inpatient, Unit Cap). The same three options,
 * with the same help text, as the ladder types' forms (Lesser-Of, Charge-Based, Professional
 * Modifiers), so "all providers" and "all payers" are said with the vocabulary the product already
 * has: a Fallback rule is every provider (optionally one payer), and a Payer-Provider rule may leave
 * its payer as All payers. Payer values follow `helpers/ruleApplicability.ts`: undefined = not
 * chosen yet, null = All payers.
 */

// PrimeReact's Dropdown shows its placeholder for a null value, so an explicit "All payers" choice
// needs a value of its own. It never leaves this file: onChange maps it back to null.
const ALL_PAYERS_VALUE = "__ALL_PAYERS__";

export const APPLICABILITY_OPTIONS: {
  value: RulesetApplicability;
  label: string;
  help: string;
}[] = [
  {
    value: "SCOPED",
    label: "Payer-Provider rule",
    help: "Applies only to the selected provider and payer.",
  },
  {
    value: "TYPICAL",
    label: "Typical rule",
    help: "Template only. No effect until copied into a contract.",
  },
  {
    value: "FALLBACK",
    label: "Fallback rule",
    help: "Live — applies to every provider that has no more specific rule of this type. Optionally limit it to a single payer.",
  },
];

const Section = styled.div`
  margin-bottom: 1rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const RadioLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FieldHelp = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

interface IRuleApplicabilityControlProps {
  applicability: RulesetApplicability;
  onApplicabilityChange: (next: RulesetApplicability) => void;
  /** The rule's payer: the Fallback payer, or a Payer-Provider rule's single payer. */
  payerId: number | null | undefined;
  onPayerChange: (payerId: number | null) => void;
  providerId?: string | null;
  onProviderChange?: (providerId: string | null) => void;
  /**
   * Replaces the built-in single provider/payer dropdowns on a Payer-Provider rule — the MPPR /
   * Case Rate / Package forms bring their own (multi-select on create, bucket chips on edit).
   */
  scopedFields?: ReactNode;
  /** Locks the built-in provider/payer dropdowns (not the applicability choice). */
  scopedFieldsLocked?: boolean;
  /**
   * Edit pages: the applicability the rule was loaded with. A Typical rule stays Typical; a
   * Payer-Provider rule and a Fallback rule can be converted into each other after a confirmation.
   * Omitted on create and copy pages, where the choice is free.
   */
  savedApplicability?: RulesetApplicability;
  /** Edit pages: why converting is not offered right now (bucket mode with the switch on). */
  conversionBlockedHint?: string;
}

export function RuleApplicabilityControl({
  applicability,
  onApplicabilityChange,
  payerId,
  onPayerChange,
  providerId = null,
  onProviderChange,
  scopedFields,
  scopedFieldsLocked,
  savedApplicability,
  conversionBlockedHint,
}: IRuleApplicabilityControlProps) {
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(
    () =>
      (providers?.data ?? []).map((provider: any) => ({
        label: provider.name,
        value: provider.id,
      })),
    [providers]
  );

  const payerOptions = useMemo(
    () => [
      { label: ALL_PAYERS_LABEL, value: ALL_PAYERS_VALUE },
      ...(payers?.data ?? []).map((payer: any) => ({
        label: payer.name,
        value: payer.id,
      })),
    ],
    [payers]
  );

  const payerLabel = (id: number | null | undefined) => {
    if (id == null) return ALL_PAYERS_LABEL;
    const payer = (payers?.data ?? []).find((p: any) => p.id === id);
    return payer?.name ?? "this payer";
  };

  const isEdit = savedApplicability !== undefined;
  // A Typical template has no scope to convert, and nothing converts into one.
  const typicalLocked = isEdit && savedApplicability === "TYPICAL";
  const conversionBlocked = isEdit && !!conversionBlockedHint;
  const isOptionDisabled = (value: RulesetApplicability) => {
    if (typicalLocked || conversionBlocked) return true;
    return isEdit && value === "TYPICAL";
  };

  const choose = (next: RulesetApplicability) => {
    if (next === applicability) return;
    if (!isEdit) {
      onApplicabilityChange(next);
      return;
    }
    const { header, message } = conversionMessage(next, payerLabel(payerId));
    confirmDialog({
      header,
      message,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Convert",
      rejectLabel: "Cancel",
      accept: () => onApplicabilityChange(next),
    });
  };

  let hint: string | null = null;
  if (conversionBlocked) {
    hint = conversionBlockedHint ?? null;
  } else if (isEdit && applicability === "SCOPED") {
    hint =
      'Convert to fallback rule: choose "Fallback rule" to apply this rule to every provider that has no more specific rule.';
  } else if (isEdit && applicability === "FALLBACK") {
    hint =
      'Convert to payer-provider rule: choose "Payer-Provider rule" and pick the provider it should apply to.';
  }

  // Undefined (nothing chosen) shows the placeholder; null is the explicit All payers choice.
  const scopedPayerValue = payerId === null ? ALL_PAYERS_VALUE : payerId;
  const toPayerId = (value: unknown): number | null =>
    value === ALL_PAYERS_VALUE || value == null ? null : (value as number);

  return (
    <Section data-testid="rule-applicability-control">
      <RadioGroup role="radiogroup" aria-label="Rule applicability">
        {APPLICABILITY_OPTIONS.map((option) => (
          <RadioLine key={option.value}>
            <RadioButton
              inputId={`applicability-${option.value}`}
              name="applicability"
              value={option.value}
              checked={applicability === option.value}
              disabled={isOptionDisabled(option.value)}
              onChange={() => choose(option.value)}
            />
            <label
              htmlFor={`applicability-${option.value}`}
              style={{ fontWeight: 600, cursor: "pointer" }}
            >
              {option.label}
            </label>
            <FieldHelp>{option.help}</FieldHelp>
          </RadioLine>
        ))}
        {hint && <FieldHelp data-testid="applicability-hint">{hint}</FieldHelp>}
      </RadioGroup>

      {applicability === "SCOPED" &&
        (scopedFields ?? (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={providerId}
                options={providerOptions}
                onChange={(e) => onProviderChange?.(e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopedFieldsLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={scopedPayerValue}
                options={payerOptions}
                onChange={(e) => onPayerChange(toPayerId(e.value))}
                placeholder="Select a payer"
                filter
                disabled={scopedFieldsLocked}
              />
              <FieldHelp>
                Choose &quot;{ALL_PAYERS_LABEL}&quot; to apply this rule to
                every payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        ))}

      {applicability === "FALLBACK" && (
        <FieldGrid>
          <Field>
            <label htmlFor="fallbackPayerId">Payer</label>
            <Dropdown
              inputId="fallbackPayerId"
              // A fallback defaults to every payer: nothing chosen reads as All payers.
              value={payerId == null ? ALL_PAYERS_VALUE : payerId}
              options={payerOptions}
              onChange={(e) => onPayerChange(toPayerId(e.value))}
              placeholder={ALL_PAYERS_LABEL}
              filter
            />
            <FieldHelp>
              Leave as &quot;{ALL_PAYERS_LABEL}&quot; for every payer. Pick a
              payer to apply this rule to that payer across every provider — a
              rule written for a specific provider still wins over this, code by
              code.
            </FieldHelp>
          </Field>
        </FieldGrid>
      )}
    </Section>
  );
}
